import axios, { AxiosRequestConfig, CreateAxiosDefaults } from 'axios';
import dayjs from 'dayjs';
import { isDev } from '@/config';
import { ResponseCode, ResponseError } from '@/types/request';
import { getLocalLanguage, getOscUuid, getToken } from './localStorage';

export interface IRequestOption extends AxiosRequestConfig {
  /** 是否返回完整响应体，默认只返回响应体中的 data */
  fullData?: boolean;
}

interface CreateRequestOptions {
  /** 开发模式 */
  dev?: boolean;
  /** 不需要鉴权的路由 */
  noAuthPaths?: string[];
  /** axios参数配置 */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  createOptions?: CreateAxiosDefaults<any>;
  /** 不包含base路径的接口 */
  excludeBaseUrlList?: string[];
}

/**
 * @Author: Mars
 * @description: 创建请求实例
 * @param {CreateRequestOptions} options
 * @return {*}
 */
const createRequest = (options: CreateRequestOptions = {}) => {
  const { createOptions = {}, noAuthPaths = [], excludeBaseUrlList = [] } = options;
  const request = axios.create({
    // 默认走同源 /api（本地经 vite 代理、生产经各自路由）；
    // staging 等需直连后端的环境，通过 VITE_API_BASE_URL 指定绝对地址（含 /api）
    baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
    timeout: 50 * 1000,
    headers: {
      platform: '89OSC8',
      'Osc-UUID': getOscUuid(),
    },
    ...createOptions,
  });

  // 拦截请求
  request.interceptors.request.use(
    (config) => {
      if (getToken() && !noAuthPaths.includes(config?.url || '')) {
        config.headers['Authorization'] = getToken();
      }

      config.headers['language'] = getLocalLanguage() || 'en-US';
      config.headers['operationDate'] = dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss');

      requestDataRemoveSpaces(config.data || config.params, config.method);
      if (excludeBaseUrlList.includes(config.url as string)) {
        config.baseURL = '';
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  // 响应拦截
  request.interceptors.response.use(
    (rep) => {
      const httpCode: number = rep.status;
      /** 如果登录状态失效，跳转至登录页 */
      switch (httpCode) {
        case 200:
          if (rep.config.responseType && rep.config.responseType === 'blob') {
            const blob: Blob = rep.data;
            const contentDisposition = rep.headers['content-disposition'];
            if (contentDisposition) {
              const fileName = window.decodeURI(contentDisposition.split(';')[1].replace(new RegExp('filename='), '').replace(new RegExp('"', 'g'), '')).trim();
              return { blob, fileName };
            } else {
              return { blob };
            }
          } else if (rep.config.responseType == 'arraybuffer') {
            let binary = '';
            const params = rep.data;
            const bytes = new Uint8Array(params);
            const len = bytes.byteLength;
            for (let i = 0; i < len; i++) {
              binary += String.fromCharCode(bytes[i]);
            }
            return binary;
          } else if ((rep.config as IRequestOption).fullData) {
            return rep.data;
          } else if (rep.data && (rep.data.code === 200 || rep.data.code === '000000')) {
            const result = rep.data.data;
            // 处理返回结果页码大于实际页码的问题
            if (result && result.pageIndex && result.pageCount) {
              if (result.pageIndex > result.pageCount) {
                if (result.total || result.total === 0) {
                  result.total++;
                }
              }
            }
            return result;
          } else if (rep.data && rep.data.status === 'OK') {
            const result = rep.data.content;
            // 处理返回结果页码大于实际页码的问题
            if (result && result.pageIndex && result.pageCount) {
              if (result.pageIndex > result.pageCount) {
                if (result.total || result.total === 0) {
                  result.total++;
                }
              }
            }
            return result;
          } else {
            if ([ResponseCode.LOGIN_EXPIRED, ResponseCode.TOKEN_IS_NULL, ResponseCode.ILLEGAL_LOGIN].includes(rep.data.code)) {
              // 详情页已支持 ?id=/?ticketNumber= 直接访问，带查询参数时完整回跳；无参数的 /detail 回跳后拿不到数据，仍降级到列表页
              const { pathname, search } = window.location;
              const redirectPath = pathname.endsWith('/detail') && !search ? pathname.slice(0, -7) : pathname + search;
              window.location.href = `/login?redirect=${encodeURIComponent(redirectPath)}`;
              return Promise.reject({ code: httpCode, msg: 'Login expired' });
            } else {
              return Promise.reject({ ...rep.data, msg: rep.data.msg || rep.data.message || rep.data.errorMsg });
            }
          }
      }
    },
    (error) => {
      const err: ResponseError = { msg: error.message, code: error.response?.status, data: null };
      return Promise.reject(err);
    },
  );

  return request;
};

/**
 * 去掉请求参数中的字符串前后空格
 * @param paramsObj 请求参数
 * @param method 请求方法
 */
function requestDataRemoveSpaces(paramsObj: Record<string, unknown>, method?: string) {
  for (const key in paramsObj) {
    const value = paramsObj[key];
    if (typeof value === 'string') {
      if (method === 'get') {
        if (value.trim().indexOf('[') > -1 || value.trim().indexOf(']') > -1) {
          paramsObj[key] = encodeURIComponent(value.trim());
        } else {
          paramsObj[key] = value.trim();
        }
      } else {
        paramsObj[key] = value.trim();
      }
    }
    if (typeof value === 'object' && value !== null && !(value instanceof File)) {
      requestDataRemoveSpaces(paramsObj[key] as Record<string, unknown>, method);
    }
  }
}

const request = createRequest({
  dev: isDev,
  // 换发 token 的入口：凭证（access_token / refresh_token）都在 body 里传，
  // 不应再带旧的 Authorization 头（旧 token 常已过期，带上反而让鉴权环节先失败）
  noAuthPaths: ['/oauth2/v1/authorize'],
});

export default request;
